import { useState, useEffect, useMemo } from 'react';
import { Award, Banknote, TrendingDown } from 'lucide-react';
import nhost from '../nhost';
import { avg, thisYear } from '../helpers';

const WINES_QUERY = `
  query {
    wines { id name vintage region price event_id created_at ratings { rating user_id } session { id completed wines_visible created_at profile { display_name } event { event_hosts { profile { display_name } } } } }
  }
`;

const FILTER_OPTIONS = [
  { key: 'session', label: 'Provningsviner' },
  { key: 'all', label: 'Alla viner' },
  { key: 'loose', label: 'Lösa viner' },
];

const cardStyle = {
  background: '#fff',
  border: '1px solid rgba(60,20,30,.08)',
  borderRadius: 16,
  padding: 16,
  marginBottom: 12,
};

const sectionTitle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 10,
};

const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

const MEDAL_COLORS = [
  { bg: '#A07830', text: '#fff' },
  { bg: '#9E9E9E', text: '#fff' },
  { bg: '#8B6340', text: '#fff' },
];

function MedalCircle({ position, size = 28 }) {
  const c = position < 3 ? MEDAL_COLORS[position] : null;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: c ? c.bg : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: c ? 13 : 13,
      fontWeight: 600,
      fontFamily: c ? 'var(--font-display)' : 'var(--font-body)',
      color: c ? c.text : 'var(--muted)',
    }}>
      {position + 1}
    </div>
  );
}

export default function Statistics({ onWineClick, onViewMember }) {
  const [wines, setWines] = useState([]);
  const [filter, setFilter] = useState('session');
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    nhost.graphql.request(WINES_QUERY).then(({ data }) => { if (data) setWines(data.wines); });
    nhost.graphql.request(`query { user_points { user_id points } profiles(where: { approved: { _eq: true } }) { id display_name } }`)
      .then(({ data }) => {
        if (!data?.user_points) return;
        const nameMap = {};
        data.profiles.forEach(p => { nameMap[p.id] = p.display_name; });
        const totals = {};
        data.user_points.forEach(p => {
          if (!totals[p.user_id]) totals[p.user_id] = { user_id: p.user_id, display_name: nameMap[p.user_id] || '?', points: 0 };
          totals[p.user_id].points += p.points;
        });
        setLeaderboard(Object.values(totals).sort((a, b) => b.points - a.points));
      });
  }, []);

  const filteredWines = useMemo(() => wines.filter(w => {
    const visible =
      (!w.session && !w.event_id) ||
      (w.session && (w.session.completed || w.session.wines_visible));
    if (!visible) return false;
    if (filter === 'session') return !!w.session;
    if (filter === 'loose') return !w.session && !w.event_id;
    return true;
  }), [wines, filter]);

  const allRatings = useMemo(() => filteredWines.flatMap(w => w.ratings.map(r => r.rating)), [filteredWines]);
  const avgAll = allRatings.length ? (allRatings.reduce((a,b) => a+b, 0) / allRatings.length).toFixed(1) : '–';
  const sessions = new Set(filteredWines.map(w => w.session?.id).filter(Boolean)).size;
  const raters = new Set(filteredWines.flatMap(w => w.ratings.map(r => r.user_id))).size;

  const withVintage = filteredWines.filter(w => w.vintage);
  const ages = withVintage.map(w => thisYear() - w.vintage);
  const avgAge = ages.length ? Math.round(ages.reduce((a,b) => a+b,0) / ages.length) : '–';
  const oldest = withVintage.length ? withVintage.reduce((a,b) => a.vintage < b.vintage ? a : b) : null;
  const youngest = withVintage.length ? withVintage.reduce((a,b) => a.vintage > b.vintage ? a : b) : null;

  const withPrice = filteredWines.filter(w => w.price != null && w.price > 0);
  const prices = withPrice.map(w => w.price);
  const maxPrice = prices.length ? Math.max(...prices) : null;
  const minPrice = prices.length ? Math.min(...prices) : null;
  const avgPrice = prices.length ? Math.round(prices.reduce((a,b) => a+b,0) / prices.length) : null;
  const mostExpensive = withPrice.length ? withPrice.reduce((a,b) => a.price > b.price ? a : b) : null;
  const cheapest = withPrice.length ? withPrice.reduce((a,b) => a.price < b.price ? a : b) : null;

  const dist = useMemo(() => [1,2,3,4,5].map(s => allRatings.filter(r => r === s).length), [allRatings]);
  const maxD = Math.max(...dist, 1);

  const topWines = useMemo(() => {
    const uniqueNames = [...new Set(filteredWines.map(w => w.name))];
    return uniqueNames.map(name => {
      const ws = filteredWines.filter(w => w.name === name);
      const rs = ws.flatMap(w => w.ratings.map(r => r.rating));
      return { id: ws[0]?.id, name, avg: rs.length ? (rs.reduce((a,b) => a+b,0)/rs.length).toFixed(1) : 0, vintage: ws[0].vintage, region: ws[0].region };
    }).sort((a,b) => b.avg - a.avg).slice(0,5);
  }, [filteredWines]);

  const repeated = useMemo(() => {
    const uniqueNames = [...new Set(filteredWines.map(w => w.name))];
    return uniqueNames.map(name => {
      const first = filteredWines.find(w => w.name === name);
      return { id: first?.id, name, count: filteredWines.filter(w => w.name === name).length, vintage: first?.vintage, region: first?.region };
    }).filter(x => x.count > 1).sort((a,b) => b.count - a.count);
  }, [filteredWines]);

  const byHost = useMemo(() => {
    const acc = {};
    filteredWines.forEach(w => {
      if (!w.session?.id) return;
      const hosts = w.session.event?.event_hosts?.map(h => h.profile?.display_name).filter(Boolean);
      const fallback = w.session.profile?.display_name;
      const names = hosts?.length ? hosts : (fallback ? [fallback] : []);
      names.forEach(name => { acc[name] = (acc[name] || 0) + 1; });
    });
    return acc;
  }, [filteredWines]);

  const yearsOfWine = useMemo(() => {
    const visibleWines = wines.filter(w =>
      (!w.session && !w.event_id) ||
      (w.session && (w.session.completed || w.session.wines_visible))
    );
    const byYear = {};
    visibleWines.forEach(w => {
      const year = w.created_at ? new Date(w.created_at).getFullYear() : null;
      if (!year) return;
      if (!byYear[year]) byYear[year] = [];
      byYear[year].push(w);
    });
    return Object.entries(byYear)
      .sort((a, b) => b[0] - a[0])
      .map(([year, yw]) => {
        const uniqueNames = [...new Set(yw.map(w => w.name))];
        const ranked = uniqueNames.map(name => {
          const ws = yw.filter(w => w.name === name);
          const rs = ws.flatMap(w => w.ratings.map(r => r.rating));
          return { id: ws[0]?.id, name, avg: rs.length ? (rs.reduce((a, b) => a + b, 0) / rs.length) : 0, count: rs.length, vintage: ws[0].vintage, region: ws[0].region };
        }).filter(w => w.count > 0).sort((a, b) => b.avg - a.avg);
        return { year, ranked };
      }).filter(y => y.ranked.length > 0);
  }, [wines]);

  return (
    <div>
      <div className="screen">
        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              style={{
                border: filter === opt.key ? 'none' : '1px solid rgba(60,20,30,.15)',
                borderRadius: 20,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                background: filter === opt.key ? 'var(--wine)' : '#fff',
                color: filter === opt.key ? '#fff' : 'var(--ink)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Översikt */}
        <div style={{ ...cardStyle, padding: '16px 16px 8px' }}>
          <div style={sectionTitle}>Översikt</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            {[
              { num: filteredWines.length, label: 'Provade viner' },
              { num: avgAll, label: 'Snittbetyg' },
              { num: sessions, label: 'Provningar' },
              { num: raters || '–', label: 'Aktiva medlemmar' },
            ].map(({ num, label }) => (
              <div key={label} style={{ background: 'var(--cream)', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{num}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pris */}
        <div style={{ ...cardStyle, padding: '16px 16px 8px' }}>
          <div style={sectionTitle}>Pris</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
            {[
              { num: maxPrice != null ? `${maxPrice} kr` : '–', label: 'Högsta' },
              { num: minPrice != null ? `${minPrice} kr` : '–', label: 'Lägsta' },
              { num: avgPrice != null ? `${avgPrice} kr` : '–', label: 'Snitt' },
            ].map(({ num, label }) => (
              <div key={label} style={{ background: 'var(--cream)', borderRadius: 12, padding: '10px 8px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{num}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {mostExpensive && (
          <div style={cardStyle}>
            <div style={sectionTitle}>Dyraste vinet</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => onWineClick?.(mostExpensive)}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Banknote size={18} color="var(--gold)" strokeWidth={1.7} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{mostExpensive.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{mostExpensive.vintage ? `${mostExpensive.vintage} · ` : ''}{mostExpensive.region}</div>
                <div style={{ fontSize: 12, color: 'var(--gold)', marginTop: 2, fontWeight: 500 }}>{mostExpensive.price} kr</div>
              </div>
            </div>
          </div>
        )}

        {cheapest && cheapest.name !== mostExpensive?.name && (
          <div style={cardStyle}>
            <div style={sectionTitle}>Billigaste vinet</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => onWineClick?.(cheapest)}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--teal-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <TrendingDown size={18} color="var(--teal)" strokeWidth={1.7} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{cheapest.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{cheapest.vintage ? `${cheapest.vintage} · ` : ''}{cheapest.region}</div>
                <div style={{ fontSize: 12, color: 'var(--teal)', marginTop: 2, fontWeight: 500 }}>{cheapest.price} kr</div>
              </div>
            </div>
          </div>
        )}

        {/* Årgångar */}
        <div style={{ ...cardStyle, padding: '16px 16px 8px' }}>
          <div style={sectionTitle}>Årgångar</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
            {[
              { num: avgAge, label: 'Snittålder (år)' },
              { num: oldest ? thisYear()-oldest.vintage : '–', label: 'Äldst (år)' },
              { num: youngest ? thisYear()-youngest.vintage : '–', label: 'Yngst (år)' },
            ].map(({ num, label }) => (
              <div key={label} style={{ background: 'var(--cream)', borderRadius: 12, padding: '10px 8px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{num}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {oldest && (
          <div style={cardStyle}>
            <div style={sectionTitle}>Äldsta vinet</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => onWineClick?.(oldest)}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Award size={18} color="var(--gold)" strokeWidth={1.7} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{oldest.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{oldest.vintage} · {oldest.region}</div>
                <div style={{ fontSize: 12, color: 'var(--gold)', marginTop: 2, fontWeight: 500 }}>{thisYear()-oldest.vintage} år · Årgång {oldest.vintage}</div>
              </div>
            </div>
          </div>
        )}

        {/* Betygsfördelning */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Betygsfördelning</div>
          {[5,4,3,2,1].map(s => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', width: 20, textAlign: 'right', fontWeight: 500 }}>{s}★</div>
              <div style={{ flex: 1, height: 7, background: 'rgba(60,20,30,.06)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round(dist[s-1]/maxD*100)}%`, background: 'var(--gold)', borderRadius: 4, transition: 'width .3s' }} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', width: 20, textAlign: 'right' }}>{dist[s-1]}</div>
            </div>
          ))}
        </div>

        {/* Topplista */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Topplista</div>
          {topWines.map((w, i) => (
            <div key={w.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', ...(i < topWines.length - 1 ? divider : {}), cursor: 'pointer' }} onClick={() => onWineClick?.(w)}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300, color: 'var(--muted)', width: 22, textAlign: 'center', flexShrink: 0 }}>{i+1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{w.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{w.vintage ? `${w.vintage} · ` : ''}{w.region}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 300, color: 'var(--wine)', flexShrink: 0 }}>{w.avg}</div>
            </div>
          ))}
        </div>

        {/* Återkommande viner */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Återkommande viner</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>Viner som provats mer än en gång</div>
          {!repeated.length && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Inga viner har provats mer än en gång ännu.</div>}
          {repeated.map((w, i) => (
            <div key={w.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', ...(i < repeated.length - 1 ? divider : {}), cursor: 'pointer' }} onClick={() => onWineClick?.(w)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{w.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{w.vintage ? `${w.vintage} · ` : ''}{w.region}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, background: 'var(--wine-light)', color: 'var(--wine)', borderRadius: 20, padding: '3px 10px' }}>Provats {w.count}×</span>
            </div>
          ))}
        </div>

        {/* Årets viner per år */}
        {yearsOfWine.map(({ year, ranked }) => (
          <div key={year} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Award size={14} color="var(--gold)" strokeWidth={1.8} />
              <div style={{ ...sectionTitle, marginBottom: 0 }}>Årets viner {year}</div>
            </div>
            {ranked.slice(0, 3).map((w, i) => {
              const c = MEDAL_COLORS[i];
              return (
                <div key={w.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', ...(i < 2 && ranked.length > i + 1 ? divider : {}), cursor: 'pointer' }} onClick={() => onWineClick?.(w)}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: c.text }}>{i+1}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-display)' }}>{w.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      {w.vintage ? `${w.vintage} · ` : ''}{w.region}
                    </div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 300, color: c.bg }}>
                    {w.avg.toFixed(1)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Poängtopplista */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Award size={14} color="var(--wine)" strokeWidth={1.8} />
            <div style={{ ...sectionTitle, marginBottom: 0 }}>Poängtopplista</div>
          </div>
          {leaderboard.length === 0 && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Laddar...</div>}
          {leaderboard.map((u, i) => (
            <div key={u.user_id} onClick={() => onViewMember?.(u.user_id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', ...(i < leaderboard.length - 1 ? divider : {}), cursor: 'pointer' }}>
              <MedalCircle position={i} />
              <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{u.display_name}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 500, color: 'var(--wine)' }}>{u.points}p</div>
            </div>
          ))}
        </div>

        {/* Mest aktiva värdar */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Mest aktiva värdar</div>
          {Object.entries(byHost).sort((a,b) => b[1]-a[1]).map(([h,c], i, arr) => (
            <div key={h} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', ...(i < arr.length - 1 ? divider : {}) }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--teal-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--teal)', fontWeight: 600, flexShrink: 0 }}>{h[0]}</div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{h}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{c} vin{c !== 1 ? 'er' : ''}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
